import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { stampAddToCartTime } from '@theme/line-item-properties';

class QuickAddInlineComponent extends Component {
  /** @type {HTMLElement | null} — the tap trigger, rendered in the gallery (outside this component) */
  #trigger = null;
  /** @type {HTMLDialogElement | null} — the variant panel, a native <dialog> */
  #dialog = null;

  connectedCallback() {
    super.connectedCallback?.();
    this.#dialog = this.querySelector('dialog');
    // The trigger lives in the gallery (so it can sit over the image), not inside
    // this component — find it within the same product card and wire it up.
    this.#trigger = this.closest('product-card, .resource-card')?.querySelector('[data-quick-add-inline="trigger"]') ?? null;
    this.#trigger?.addEventListener('click', this.#handleTriggerClick);
    // Native <dialog>: reset the trigger state whenever it closes (ESC, backdrop, or after add).
    this.#dialog?.addEventListener('close', this.#handleDialogClose);
    this.#dialog?.addEventListener('click', this.#handleDialogClick);
    // The nested product-form-component fires these (both bubble) when the add-to-cart
    // request settles — clear the loading state then.
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(ThemeEvents.cartError, this.#stopLoading);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#trigger?.removeEventListener('click', this.#handleTriggerClick);
    this.#dialog?.removeEventListener('close', this.#handleDialogClose);
    this.#dialog?.removeEventListener('click', this.#handleDialogClick);
    this.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(ThemeEvents.cartError, this.#stopLoading);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    if (this.#dialog?.open) document.documentElement.removeAttribute('scroll-lock');
  }

  #stopLoading = () => this.removeAttribute('aria-busy');

  /**
   * Reveals the panel for keyboard use. A CSS `:has(:focus-visible)` rule cannot do this,
   * because the match lapses as focus moves between radios and restarts the fade.
   *
   * @param {FocusEvent} event
   */
  #handleFocusIn = (event) => {
    if (event.target instanceof Element && event.target.matches(':focus-visible')) {
      this.#dialog?.setAttribute('data-keyboard-open', '');
    }
  };

  /**
   * Closes the keyboard reveal, unless focus is only moving between the sizes.
   *
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#dialog?.removeAttribute('data-keyboard-open');
  };

  /**
   * On touch the cart drawer opens over this modal and leaves it open underneath, so
   * dismissing the drawer would reveal it again. A failed add keeps it open so the error
   * stays visible; on hover devices the panel is revealed without `open`, so close() no-ops.
   *
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    this.#stopLoading();

    const cartAddEvent = /** @type {import('@theme/events').CartAddEvent} */ (event);
    if (cartAddEvent.detail?.data?.didError) return;

    this.#dialog?.close();
  };

  /** @param {Event} event */
  #handleTriggerClick = (event) => this.toggleOpen(event);

  /**
   * Opens the panel on touch devices (no hover) as a real modal via showModal(),
   * so it's focus-trapped and ESC-closable, styled as a bottom-sheet with the
   * native ::backdrop as the scrim (see the CSS in quick-add-inline.liquid).
   * @param {Event} [event]
   */
  toggleOpen(event) {
    if (event) event.preventDefault();
    if (!this.#dialog) return;
    if (this.#dialog.open) {
      this.#dialog.close();
      return;
    }
    this.#dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    this.#trigger?.setAttribute('aria-expanded', 'true');
  }

  #handleDialogClose = () => {
    document.documentElement.removeAttribute('scroll-lock');
    this.#trigger?.setAttribute('aria-expanded', 'false');
  };

  /** Close when the backdrop (the dialog element itself) is clicked. @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    // The dialog paints in the top layer but is still a DOM child of the product
    // card's <a>, so any click inside it would bubble up and navigate to the PDP.
    // Stop it here; only a tap on the dialog's own (backdrop) area closes.
    event.stopPropagation();
    if (event.target === this.#dialog) this.#dialog.close();
  };

  /**
   * Adds the activated size to the cart. Arrow keys are excluded because a radio group fires
   * a click of its own, with `detail` 0, as the selection moves.
   *
   * @param {Event} event - The radio's click or keydown
   */
  handleSizeActivate(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const isKeyPress = event instanceof KeyboardEvent && (event.key === 'Enter' || event.key === ' ');
    if (!isKeyPress && !(event instanceof MouseEvent && event.detail > 0)) return;
    if (isKeyPress) event.preventDefault();

    if (input.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    // Enter has no activation behaviour on a radio.
    input.checked = true;

    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput || !input.value) return;

    variantIdInput.value = input.value;
    variantIdInput.removeAttribute('disabled');

    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('form'));
    if (!form) return;

    stampAddToCartTime(form);
    this.setAttribute('aria-busy', 'true');
    form.requestSubmit();
  }
}

if (!customElements.get('quick-add-inline-component')) {
  customElements.define('quick-add-inline-component', QuickAddInlineComponent);
}
